// ─── Image Roles (Phase 1.8) ──────────────────────────────────
// Adds an optional `role` field on image elements to capture intent:
//   - sectionBackground  — full-bleed background (locked, behind everything)
//   - paperCard          — foreground card / parchment overlay
//   - photo              — guest/couple photo
//   - decorativeElement  — wax seals, ornaments, monograms
//   - imageLayer         — generic image (legacy/default for existing elements)
//
// Roles are additive — public renderer treats unknown / missing roles as
// `imageLayer` and renders identically to today.

export const IMAGE_ROLES = {
  SECTION_BACKGROUND: 'sectionBackground',
  PAPER_CARD: 'paperCard',
  PHOTO: 'photo',
  DECORATIVE: 'decorativeElement',
  IMAGE_LAYER: 'imageLayer',
};

/**
 * Defaults for each role — used when adding a NEW image with a chosen role.
 * Existing elements are NEVER auto-rewritten by this helper.
 */
export function getImageRoleDefaults(role) {
  switch (role) {
    case IMAGE_ROLES.SECTION_BACKGROUND:
      return {
        xPercent: 50, yPercent: 50, widthPercent: 100,
        anchor: 'c',
        fit: 'cover',
        locked: true,
        zIndex: 0,
      };
    case IMAGE_ROLES.PAPER_CARD:
      return {
        // Card layer: centered horizontally, near top, ~86% wide, contain fit
        // so PNG/SVG transparency is preserved. Sits above the bg (z=0) but
        // below typical text/photos (which sit at z >= 3).
        xPercent: 50, yPercent: 4, widthPercent: 86,
        anchor: 't',
        fit: 'contain',
        locked: false,
        zIndex: 2,
      };
    case IMAGE_ROLES.PHOTO:
      return {
        xPercent: 50, yPercent: 50, widthPercent: 60,
        anchor: 'c',
        fit: 'cover',
        locked: false,
        zIndex: 3,
      };
    case IMAGE_ROLES.DECORATIVE:
      return {
        xPercent: 50, yPercent: 50, widthPercent: 30,
        anchor: 'c',
        fit: 'contain',
        locked: false,
        zIndex: 3,
      };
    case IMAGE_ROLES.IMAGE_LAYER:
    default:
      return {
        xPercent: 50, yPercent: 50, widthPercent: 50,
        anchor: 'c',
        fit: 'contain',
        locked: false,
        zIndex: 3,
      };
  }
}

/**
 * Apply a role's defaults onto an element. Used when the designer EXPLICITLY
 * picks a role (either on upload or via the role switcher in ElementStylePanel).
 * Preserves the element's existing position when `preserveExistingPosition`
 * is true (default) — useful when changing role on an already-positioned image.
 */
export function applyImageRole(element, role, { preserveExistingPosition = true } = {}) {
  if (!element) return element;
  const defaults = getImageRoleDefaults(role);
  const mobile = (element.layout && element.layout.mobile) || {};
  const desktop = (element.layout && element.layout.desktop) || {};

  const nextMobile = {
    ...mobile,
    xPercent: preserveExistingPosition && mobile.xPercent != null ? mobile.xPercent : defaults.xPercent,
    yPercent: preserveExistingPosition && mobile.yPercent != null ? mobile.yPercent : defaults.yPercent,
    widthPercent: preserveExistingPosition && mobile.widthPercent != null ? mobile.widthPercent : defaults.widthPercent,
    anchor: mobile.anchor || defaults.anchor,
    // Also write legacy x/y/w so the save adapter and legacy renderer stay in sync.
    x: preserveExistingPosition && mobile.x != null ? mobile.x : defaults.xPercent / 100,
    y: preserveExistingPosition && mobile.y != null ? mobile.y : defaults.yPercent / 100,
    w: preserveExistingPosition && mobile.w != null ? mobile.w : defaults.widthPercent / 100,
    z: defaults.zIndex,
  };

  return {
    ...element,
    role,
    locked: element.locked != null ? element.locked : defaults.locked,
    customStyle: {
      ...(element.customStyle || {}),
      fit: (element.customStyle && element.customStyle.fit) || defaults.fit,
      // Preserve transparency for card/decorative roles so PNG/SVG passes through.
      transparent: role === IMAGE_ROLES.PAPER_CARD || role === IMAGE_ROLES.DECORATIVE
        ? true
        : (element.customStyle && element.customStyle.transparent) || false,
    },
    layout: {
      ...(element.layout || {}),
      mobile: nextMobile,
      // Mirror to desktop only if it already exists; don't fabricate a desktop
      // layout for an element that didn't have one.
      desktop: Object.keys(desktop).length
        ? { ...desktop, ...(preserveExistingPosition ? {} : {
            xPercent: defaults.xPercent, yPercent: defaults.yPercent, widthPercent: defaults.widthPercent,
            x: defaults.xPercent / 100, y: defaults.yPercent / 100, w: defaults.widthPercent / 100,
            anchor: defaults.anchor, z: defaults.zIndex,
          }) }
        : desktop,
    },
  };
}

/** Quick action — make a Card layer fit the mobile canvas at 86% width. */
export function fitCardToMobileWidth(element) {
  const mobile = (element.layout && element.layout.mobile) || {};
  return {
    ...element,
    layout: {
      ...(element.layout || {}),
      mobile: {
        ...mobile,
        xPercent: 50, widthPercent: 86, anchor: 't',
        x: 0.5, w: 0.86,
      },
    },
  };
}

/**
 * One-tap "Lock & fit" for Card layer (Phase 1.8 polish).
 * Centers horizontally at top-anchor, sets 86% width, contain fit, and locks.
 * Preserves existing yPercent (defaults to 4 if missing) and zIndex
 * (defaults to 2 if missing). Only intended for paperCard role — callers
 * gate the UI accordingly.
 */
export function lockAndFitCard(element) {
  if (!element) return element;
  const mobile = (element.layout && element.layout.mobile) || {};
  const existingY = mobile.yPercent != null ? mobile.yPercent : (mobile.y != null ? mobile.y * 100 : null);
  const yPercent = existingY != null ? existingY : 4;
  const existingZ = mobile.z;
  const zIndex = typeof existingZ === 'number' ? existingZ : 2;

  return {
    ...element,
    role: IMAGE_ROLES.PAPER_CARD,
    locked: true,
    customStyle: {
      ...(element.customStyle || {}),
      fit: 'contain',
      transparent: true,
    },
    layout: {
      ...(element.layout || {}),
      mobile: {
        ...mobile,
        xPercent: 50,
        yPercent,
        widthPercent: 86,
        anchor: 't',
        // Keep legacy x/y/w in sync for the save adapter.
        x: 0.5,
        y: yPercent / 100,
        w: 0.86,
        z: zIndex,
      },
    },
  };
}

export const IMAGE_ROLE_OPTIONS = [
  { value: IMAGE_ROLES.PHOTO,              label: 'Photo',          desc: 'Guest or couple photo' },
  { value: IMAGE_ROLES.SECTION_BACKGROUND, label: 'Background',     desc: 'Full-bleed backdrop' },
  { value: IMAGE_ROLES.PAPER_CARD,         label: 'Card layer',     desc: 'Centered card/paper layer' },
  { value: IMAGE_ROLES.DECORATIVE,         label: 'Decorative layer', desc: 'Wax seal, monogram, ornament' },
  { value: IMAGE_ROLES.IMAGE_LAYER,        label: 'Image layer',    desc: 'Generic image' },
];
